import * as React from "react";

import "./table.css";

/**
 * The Table family — Table, TableContainer, THead, TBody, TFoot, TR, TH, TD — replacing
 * `@salt-ds/core`'s. Structural only: no floating layer, no state beyond one overflow check.
 *
 * ── THE CONTAINER AND THE TABLE TALK TO EACH OTHER, AND IT IS FOR A REASON ────────────
 *
 * `TableContainer` is the scrolling box. When the table overflows it, the container becomes a
 * focusable, labelled region — `role="region"`, `tabIndex={0}` — because otherwise a keyboard
 * user cannot scroll a wide table at all: there is nothing focusable inside a grid of text and
 * the scrollbar is not reachable.
 *
 * To be labelled it needs a name, and the name belongs to the TABLE. So the table publishes its
 * id upward through a context and the container points `aria-labelledby` at it. That is the
 * whole purpose of the context — not styling, not layout.
 *
 * The label is only applied when it overflows. A `role="region"` on every table would add a
 * landmark and a tab stop to each one, which turns a page of small tables into a page of
 * announcements.
 *
 * ── ALIGNMENT IS TWO CLASSES ──────────────────────────────────────────────────────────
 *
 * `TD` and `TH` emit both `saltTable-td` and `saltTable-td-align-left`. The second alone
 * matches nothing without the first, and text-align is not inheritable from a table in the way
 * a naive port assumes.
 */
/*
 * Read from the vendor's `.d.ts`, not inferred from its implementation. Both unions were too
 * narrow on the first attempt and `src/components/ui/table.tsx` uses the missing members:
 * `divider` has a "none", and `variant` has a "tertiary". This is the fourth time in this
 * migration that reading the implementation instead of the declaration produced a silently
 * narrower API — the implementation shows what a value DOES, never what values are allowed.
 */
type Divider = "primary" | "secondary" | "tertiary" | "none";
type Variant = "primary" | "secondary" | "tertiary";
type TextAlign = "left" | "right";

interface TableContextValue {
  id?: string;
  setId: (id: string) => void;
  labelledBy?: string;
  setLabelledBy: (id: string) => void;
}

const TableContext = React.createContext<TableContextValue>({
  setId: () => undefined,
  setLabelledBy: () => undefined,
});

export function useTable(): TableContextValue {
  return React.useContext(TableContext);
}

export interface TableContainerProps extends React.HTMLAttributes<HTMLDivElement> {}

export const TableContainer = React.forwardRef<HTMLDivElement, TableContainerProps>(
  function TableContainer(
    {
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      children,
      className,
      role,
      tabIndex,
      ...rest
    },
    ref,
  ) {
    const [isOverflowing, setIsOverflowing] = React.useState(false);
    const [tableId, setTableId] = React.useState<string | undefined>(undefined);
    const [labelledBy, setLabelledBy] = React.useState<string | undefined>(undefined);
    const scrollRef = React.useRef<HTMLDivElement | null>(null);

    const setRefs = React.useCallback(
      (node: HTMLDivElement | null) => {
        scrollRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      },
      [ref],
    );

    const checkOverflow = React.useCallback(() => {
      const element = scrollRef.current;
      if (!element) return;
      setIsOverflowing(
        element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth,
      );
    }, []);

    React.useLayoutEffect(() => {
      checkOverflow();
      const element = scrollRef.current;
      if (!element || typeof ResizeObserver === "undefined") return;
      const observer = new ResizeObserver(checkOverflow);
      observer.observe(element);
      return () => observer.disconnect();
    }, [checkOverflow]);

    /*
     * A caller's own label always wins. The table's id is only borrowed when the caller has
     * supplied neither `aria-label` nor `aria-labelledby` — otherwise a container with a
     * deliberate name would be silently renamed to whatever the table happens to be called.
     */
    const overflowProps = isOverflowing
      ? {
          role: role ?? "region",
          tabIndex: tabIndex ?? 0,
          "aria-label": ariaLabel,
          ...(ariaLabelledBy === undefined &&
            ariaLabel === undefined && { "aria-labelledby": labelledBy ?? tableId }),
          ...(ariaLabelledBy !== undefined && { "aria-labelledby": ariaLabelledBy }),
        }
      : { role, tabIndex, "aria-labelledby": ariaLabelledBy, "aria-label": ariaLabel };

    // Not memoised: the setters are stable but `id`/`labelledBy` change, and a stale context
    // here would leave the container pointing at nothing. Cheap — this renders once per table.
    const contextValue: TableContextValue = {
      id: tableId,
      setId: setTableId,
      labelledBy,
      setLabelledBy,
    };

    return (
      <TableContext.Provider value={contextValue}>
        <div
          className={className ? `saltTable-container ${className}` : "saltTable-container"}
          ref={setRefs}
          {...overflowProps}
          {...rest}
        >
          {children}
        </div>
      </TableContext.Provider>
    );
  },
);

export interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  variant?: Variant;
  divider?: Divider;
  zebra?: boolean;
}

export const Table = React.forwardRef<HTMLTableElement, TableProps>(function Table(
  {
    "aria-labelledby": ariaLabelledBy,
    children,
    className,
    divider = "tertiary",
    id,
    variant = "primary",
    zebra = false,
    ...rest
  },
  ref,
) {
  const generatedId = React.useId();
  const tableId = id ?? generatedId;
  const { setId, setLabelledBy } = useTable();
  const labelledBy = ariaLabelledBy ?? tableId;

  React.useLayoutEffect(() => {
    // Publishing upward is what lets the scrolling container name itself after this table.
    if (tableId) setId(tableId);
    if (labelledBy) setLabelledBy(labelledBy);
  }, [tableId, labelledBy, setId, setLabelledBy]);

  const classes = ["saltTable", `saltTable-${variant}`];
  if (zebra) classes.push("saltTable-zebra");
  classes.push(`saltTable-divider-${divider}`);
  if (className) classes.push(className);

  return (
    <table aria-labelledby={labelledBy} className={classes.join(" ")} id={tableId} ref={ref} {...rest}>
      {children}
    </table>
  );
});

export interface TableSectionProps
  extends React.HTMLAttributes<HTMLTableSectionElement> {
  sticky?: boolean;
  variant?: Variant;
  divider?: Divider;
}

function section(tag: "thead" | "tfoot", base: "thead" | "tfoot", displayName: string) {
  const Component = React.forwardRef<HTMLTableSectionElement, TableSectionProps>(function Section(
    { children, className, divider = "primary", sticky = false, variant, ...rest },
    ref,
  ) {
    const classes = [`saltTable-${base}`];
    if (variant) classes.push(`saltTable-${base}-${variant}`);
    if (sticky) classes.push(`saltTable-${base}-sticky`);
    if (divider) classes.push(`saltTable-${base}-divider-${divider}`);
    if (className) classes.push(className);
    const Tag = tag;
    return (
      <Tag className={classes.join(" ")} ref={ref} {...rest}>
        {children}
      </Tag>
    );
  });
  Component.displayName = displayName;
  return Component;
}

export const THead = section("thead", "thead", "THead");
export const TFoot = section("tfoot", "tfoot", "TFoot");

export type TBodyProps = React.HTMLAttributes<HTMLTableSectionElement>;

export const TBody = React.forwardRef<HTMLTableSectionElement, TBodyProps>(function TBody(
  { children, className, ...rest },
  ref,
) {
  /*
   * `saltTable-tbody` is kept even though no vendor stylesheet defines it — and that is a
   * DIFFERENT call from `saltSwitch-icon` and `saltDialogHeader-description`, which were
   * dropped for the same reason. The difference is that this one is ASSERTED:
   * `src/components/ui/table.test.tsx` pins `saltTable-tr` directly, which makes it a contract
   * a consumer can select on, not merely residue. Both are declared as structural hooks in
   * `table.css` so the dead-class guard sees them defined rather than being told to ignore them.
   */
  return (
    <tbody className={className ? `saltTable-tbody ${className}` : "saltTable-tbody"} ref={ref} {...rest}>
      {children}
    </tbody>
  );
});

export type TRProps = React.HTMLAttributes<HTMLTableRowElement>;

export const TR = React.forwardRef<HTMLTableRowElement, TRProps>(function TR(
  { children, className, ...rest },
  ref,
) {
  // See TBody above: kept because a test pins it as a contract.
  return (
    <tr className={className ? `saltTable-tr ${className}` : "saltTable-tr"} ref={ref} {...rest}>
      {children}
    </tr>
  );
});

export interface TCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  textAlign?: TextAlign;
}

function cell(tag: "td" | "th", displayName: string) {
  const Component = React.forwardRef<HTMLTableCellElement, TCellProps>(function Cell(
    { children, className, textAlign = "left", ...rest },
    ref,
  ) {
    // BOTH classes: the alignment modifier matches nothing on its own.
    const classes = [`saltTable-${tag}`, `saltTable-${tag}-align-${textAlign}`];
    if (className) classes.push(className);
    const Tag = tag;
    return (
      <Tag className={classes.join(" ")} ref={ref} {...rest}>
        {children}
      </Tag>
    );
  });
  Component.displayName = displayName;
  return Component;
}

export const TD = cell("td", "TD");
export const TH = cell("th", "TH");
